import type { ReactNode } from "react"
import { useCallback, useEffect, useMemo, useState } from "react"
import {
	IconCheckOutline18 as Check,
	IconChevronRightOutline18 as ChevronRight,
	IconBarsFilterOutline18 as ListFilterIcon,
} from "nucleo-ui-outline-18"

import type { NestedCommandGroup } from "@productlane/ui/src/components/internal/nested-command"
import { cn } from "@productlane/lib/src/cn"
import { IconButton, Popover, Shortcut, Tooltip } from "@productlane/ui"
import {
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@productlane/ui/src/components/internal/command"
import {
	NESTED_COMMAND_LIST_CLASS,
	NestedCommand,
} from "@productlane/ui/src/components/internal/nested-command"

import type { SearchableFilterItem } from "./types"
import { useShortcut } from "@/lib"

export interface FilterPopoverProps<T extends string> {
	categories: NestedCommandGroup<T>[]
	counts: Record<T, number>
	renderSubPanel: (
		group: T,
		query: string,
		setQuery: (q: string) => void,
		onFilterItemSelect: () => void,
	) => ReactNode
	searchableItems: SearchableFilterItem<T>[]
	filteredSearchItems: SearchableFilterItem<T>[]
	onGlobalQueryChange?: (q: string) => void
	onCategorySelect?: (type: T) => void
	subPanelWidthClassName?: (group: T) => string
	mainPanelWidthClassName?: (showGlobalSearch: boolean) => string
	subPanelSide?: "left" | "right"
	dotBadgeCategories?: ReadonlySet<T>
	shortcutKey?: string
	/** A second key that opens the popover straight onto one group. */
	groupShortcut?: { key: string; group: T }
	tooltipLabel?: string
	align?: "start" | "end"
	trigger?: ReactNode
	iconClassName?: string
	iconStrokeWidth?: number
	onOpen?: () => void
	onOpenChange?: (open: boolean) => void
	closeOnSelect?: boolean
}

export function FilterPopover<T extends string>({
	categories,
	counts,
	renderSubPanel,
	searchableItems: _searchableItems,
	filteredSearchItems,
	onGlobalQueryChange,
	onCategorySelect,
	subPanelWidthClassName,
	mainPanelWidthClassName,
	subPanelSide = "left",
	dotBadgeCategories,
	shortcutKey = "f",
	groupShortcut,
	tooltipLabel = "Add filter",
	closeOnSelect = false,
	align = "end",
	trigger,
	iconClassName,
	iconStrokeWidth = 1.75,
	onOpen,
	onOpenChange,
}: FilterPopoverProps<T>) {
	const [isOpen, setIsOpen] = useState(false)
	useEffect(() => {
		onOpenChange?.(isOpen)
	}, [isOpen, onOpenChange])
	const [query, setQuery] = useState("")
	const [activeFilter, setActiveFilter] = useState<T | null>(null)
	const [globalQuery, setGlobalQuery] = useState("")

	const handleCategorySelect = useCallback(
		(type: T) => {
			setActiveFilter(type)
			setGlobalQuery("")
			setQuery("")
			onGlobalQueryChange?.("")
			onCategorySelect?.(type)
		},
		[onCategorySelect, onGlobalQueryChange],
	)

	useShortcut(
		shortcutKey,
		() => {
			setIsOpen(true)
		},
		{ preventDefault: true },
	)

	useShortcut(
		groupShortcut?.key ?? "",
		() => {
			if (!groupShortcut) return
			setIsOpen(true)
			setActiveFilter(groupShortcut.group)
		},
		{ preventDefault: true, enabled: !!groupShortcut },
	)

	// The reset waits for the close animation so the panel does not jump.
	const closePopover = useCallback(() => {
		setIsOpen(false)
		setTimeout(() => {
			setActiveFilter(null)
			setQuery("")
			setGlobalQuery("")
			onGlobalQueryChange?.("")
		}, 100)
	}, [onGlobalQueryChange])

	const onFilterItemSelect = useCallback(() => {
		setQuery("")
		if (closeOnSelect) closePopover()
	}, [closeOnSelect, closePopover])

	const handleGroupChange = useCallback((group: T | null) => {
		setActiveFilter(group)
		if (group) setQuery("")
	}, [])

	const categoriesWithBadges = useMemo(
		() =>
			categories.map((cat) => {
				const count = counts[cat.type] ?? 0
				if (count === 0) return { ...cat, badge: undefined }
				return {
					...cat,
					badge: dotBadgeCategories?.has(cat.type) ? (
						<span className="h-1.5 w-1.5 rounded-full bg-brand-600 dark:bg-brand-400" />
					) : (
						<span className="min-w-4 rounded bg-gray-200 px-1 text-center text-xxs font-medium text-brand-700 dark:bg-gray-700 dark:text-brand-400">
							{count}
						</span>
					),
				}
			}),
		[categories, counts, dotBadgeCategories],
	)

	const showGlobalSearch = globalQuery.length > 0

	const renderSubContent = useCallback(
		(group: T) => renderSubPanel(group, query, setQuery, onFilterItemSelect),
		[renderSubPanel, query, onFilterItemSelect],
	)

	const mainPanelClass = mainPanelWidthClassName
		? mainPanelWidthClassName(showGlobalSearch)
		: showGlobalSearch
			? "w-80"
			: "w-52"

	const subPanelClass =
		activeFilter && subPanelWidthClassName
			? subPanelWidthClassName(activeFilter)
			: "w-64"

	return (
		<Popover
			className="w-auto overflow-visible p-0"
			align={align}
			open={isOpen}
			onOpenChange={(value) => {
				if (value) {
					setIsOpen(true)
					onOpen?.()
				} else {
					closePopover()
				}
			}}
			onOpenAutoFocus={(e) => {
				e.preventDefault()
			}}
			content={
				<NestedCommand<T>
					groups={categoriesWithBadges}
					activeGroup={showGlobalSearch ? null : activeFilter}
					onGroupChange={handleGroupChange}
					header={
						<CommandInput
							autoFocus
							placeholder="Filter..."
							value={globalQuery}
							onValueChange={(v) => {
								setGlobalQuery(v)
								onGlobalQueryChange?.(v)
								if (v) setActiveFilter(null)
							}}
						/>
					}
					mainPanelClassName={mainPanelClass}
					subPanelClassName={subPanelClass}
					subPanelSide={subPanelSide}
					renderSubContent={renderSubContent}
				>
					{showGlobalSearch ? (
						<CommandList className={NESTED_COMMAND_LIST_CLASS}>
							<CommandGroup>
								{filteredSearchItems.length === 0 && (
									<CommandEmpty>No results found.</CommandEmpty>
								)}
								{filteredSearchItems.map((item) => (
									<CommandItem
										key={item.id}
										onSelect={() => {
											item.onSelect()
											setGlobalQuery("")
											onGlobalQueryChange?.("")
											if (item.id.startsWith("category-") && onCategorySelect) {
												handleCategorySelect(item.category)
											} else if (closeOnSelect) {
												closePopover()
											}
										}}
									>
										<div className="flex w-full items-center gap-2">
											{item.renderCustom ? (
												item.renderCustom()
											) : item.Icon ? (
												<item.Icon
													className="h-4 w-4 shrink-0 text-brand-600 dark:text-brand-500 [&:not(.lucide)]:fill-brand-600 [&:not(.lucide)]:dark:fill-brand-500"
													style={
														item.iconColor
															? { color: item.iconColor }
															: undefined
													}
												/>
											) : null}
											<div className="flex min-w-0 flex-1 items-center gap-1">
												<span className="shrink-0 text-secondary">
													{item.categoryLabel}
												</span>
												<ChevronRight className="h-3 w-3 shrink-0 text-tertiary" />
												<span className="truncate text-primary">
													{item.label}
												</span>
												{item.secondaryLabel ? (
													<span className="truncate text-secondary">
														{item.secondaryLabel}
													</span>
												) : null}
											</div>
											<Check
												className={cn("h-4 w-4 shrink-0", {
													"opacity-100": item.isSelected,
													"opacity-0": !item.isSelected,
												})}
											/>
										</div>
									</CommandItem>
								))}
							</CommandGroup>
						</CommandList>
					) : null}
				</NestedCommand>
			}
		>
			{trigger ?? (
				<div>
					<Tooltip
						label={
							<div className="flex items-center gap-1">
								{tooltipLabel}
								<Shortcut>{shortcutKey.toUpperCase()}</Shortcut>
							</div>
						}
					>
						<IconButton
							variant="ghost"
							Icon={ListFilterIcon}
							iconStrokeWidth={iconStrokeWidth}
							iconClassName={iconClassName}
						/>
					</Tooltip>
				</div>
			)}
		</Popover>
	)
}
